import { useQuery, keepPreviousData } from '@tanstack/react-query';
import axios from 'axios';
import { ScrollText, Loader2, RefreshCw, ChevronLeft, ChevronRight, ChevronDown, ChevronRight as ChevronRightSmall, Search } from 'lucide-react';
import { useState } from 'react';
import Layout from '../components/Layout';

const DOMAIN_COLORS = {
  auth: 'bg-purple-50 text-purple-600 border-purple-200',
  settings: 'bg-amber-50 text-amber-600 border-amber-200',
  users: 'bg-blue-50 text-blue-600 border-blue-200',
  locations: 'bg-teal-50 text-teal-600 border-teal-200',
  bookings: 'bg-green-50 text-green-600 border-green-200',
  reminders: 'bg-pink-50 text-pink-600 border-pink-200',
  dayoffs: 'bg-orange-50 text-orange-600 border-orange-200',
};

const PAGE_SIZE = 50;

function fmtTime(utc) {
  if (!utc) return '';
  // UTC — ISO-8601 or legacy "YYYY-MM-DD HH:MM:SS"; render in Dubai time.
  const s = String(utc);
  const d = new Date(s.endsWith('Z') ? s : s.replace(' ', 'T') + 'Z');
  if (isNaN(d)) return utc;
  return d.toLocaleString('en-GB', { timeZone: 'Asia/Dubai', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
}

function actorLabel(r) {
  if (r.actor_type === 'customer') return 'customer';
  if (r.actor_type === 'system') return 'system';
  return r.user_email || `user #${r.user_id ?? '?'}`;
}

function fmtVal(v) {
  if (v === undefined || v === null) return '∅';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

// Recursively find the exact leaf paths that differ between before/after so the
// change is pinpointed in the JSON (e.g. "positions.0.amc.start"), not just shown
// as two big blobs.
function deepDiff(before, after, base = '') {
  const out = [];
  const isObj = (v) => v && typeof v === 'object';
  if (!isObj(before) || !isObj(after)) {
    if (JSON.stringify(before ?? null) !== JSON.stringify(after ?? null)) out.push({ path: base, before, after });
    return out;
  }
  for (const k of new Set([...Object.keys(before), ...Object.keys(after)])) {
    const p = base ? `${base}.${k}` : k;
    const bv = before[k];
    const av = after[k];
    if (isObj(bv) && isObj(av)) out.push(...deepDiff(bv, av, p));
    else if (JSON.stringify(bv ?? null) !== JSON.stringify(av ?? null)) out.push({ path: p, before: bv, after: av });
  }
  return out;
}

function ChangeRow({ change }) {
  const leaves = deepDiff(change.before, change.after, '');
  const rows = leaves.length ? leaves : [{ path: '', before: change.before, after: change.after }];
  return (
    <div className="text-[11px] py-1.5 border-b border-brand-light/40 last:border-0">
      <span className="font-mono font-semibold text-brand-dark">{change.key}</span>
      <span className="ml-1.5 text-[9px] text-brand-neutral">{rows.length} field{rows.length === 1 ? '' : 's'} changed</span>
      <div className="mt-1 space-y-0.5">
        {rows.map((d, i) => (
          <div key={i} className="flex flex-wrap items-center gap-1.5 pl-2">
            {d.path && <span className="font-mono text-[10px] text-brand-primary">{d.path}</span>}
            <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-100 line-through break-all">{fmtVal(d.before)}</span>
            <span className="text-brand-neutral">→</span>
            <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-green-50 text-green-700 border border-green-100 break-all">{fmtVal(d.after)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MetaView({ metadata }) {
  let meta = null;
  try { meta = metadata ? JSON.parse(metadata) : null; } catch { meta = null; }
  if (!meta) return <p className="text-xs text-brand-neutral">No detail.</p>;
  const hasChanges = Array.isArray(meta.changes) && meta.changes.length > 0;

  return (
    <div className="space-y-2">
      {hasChanges && (
        <div>
          <p className="text-[10px] font-semibold text-brand-neutral mb-1">WHAT CHANGED ({meta.changes.length})</p>
          <div className="bg-white border border-brand-light rounded-lg px-2 py-1">
            {meta.changes.map((c, i) => <ChangeRow key={i} change={c} />)}
          </div>
        </div>
      )}
      <details>
        <summary className="text-[10px] text-brand-neutral cursor-pointer">{hasChanges ? 'Full metadata' : 'Metadata'}</summary>
        <pre className="mt-1 bg-[#f5f8f6] border border-brand-light rounded p-2 overflow-x-auto text-[10px] text-brand-dark whitespace-pre-wrap break-all">{JSON.stringify(meta, null, 2)}</pre>
      </details>
    </div>
  );
}

export default function AuditLog() {
  const [filters, setFilters] = useState({ domain: 'all', action: '', userEmail: '', q: '', from: '', to: '' });
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState(null);

  const setFilter = (key, value) => { setFilters((f) => ({ ...f, [key]: value })); setOffset(0); };

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['audit', filters, offset],
    queryFn: async () => {
      const params = { limit: PAGE_SIZE, offset };
      for (const [k, v] of Object.entries(filters)) if (v) params[k] = v;
      const { data } = await axios.get('/api/audit', { params });
      return data;
    },
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  });

  const rows = data?.rows || [];
  const total = data?.total || 0;
  const domains = data?.domains || [];
  const actions = data?.actions || [];

  return (
    <Layout>
      <header className="bg-white border-b border-brand-light/60 p-3 md:p-4 flex justify-between items-center z-10 shadow-sm relative">
        <div className="flex items-center gap-2 md:gap-3">
          <div className="w-9 h-9 bg-gradient-to-br from-brand-light to-white rounded-xl flex items-center justify-center shadow-sm">
            <ScrollText className="w-5 h-5 text-brand-primary" />
          </div>
          <div>
            <h1 className="text-base md:text-xl font-bold text-brand-dark tracking-tight">Audit Log</h1>
            <p className="text-xs text-brand-neutral hidden md:block">Every action recorded across the AMC system — 60-day history</p>
          </div>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-brand-dark bg-white border border-brand-light rounded-xl hover:bg-brand-light transition-colors shadow-sm disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </header>

      <main className="flex-1 overflow-y-auto p-3 md:p-6 bg-[#f5f8f6]">
        <div className="max-w-6xl mx-auto space-y-4">
          {/* Filters */}
          <div className="bg-white rounded-2xl shadow-sm border border-brand-light p-3 flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[160px] relative">
              <Search className="w-3.5 h-3.5 text-brand-neutral absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                type="text" placeholder="Search summary / metadata…" value={filters.q}
                onChange={(e) => setFilter('q', e.target.value)}
                className="w-full pl-8 pr-3 py-2 border border-brand-light rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
              />
            </div>
            <select value={filters.domain} onChange={(e) => setFilter('domain', e.target.value)} className="px-3 py-2 border border-brand-light rounded-xl text-sm bg-white">
              <option value="all">All domains</option>
              {domains.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <select value={filters.action} onChange={(e) => setFilter('action', e.target.value)} className="px-3 py-2 border border-brand-light rounded-xl text-sm bg-white">
              <option value="">All actions</option>
              {actions.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
            <input type="text" placeholder="user email" value={filters.userEmail} onChange={(e) => setFilter('userEmail', e.target.value)} className="px-3 py-2 border border-brand-light rounded-xl text-sm bg-white w-[140px]" />
            <input type="date" value={filters.from} onChange={(e) => setFilter('from', e.target.value)} title="From" className="px-2 py-2 border border-brand-light rounded-xl text-sm bg-white" />
            <input type="date" value={filters.to} onChange={(e) => setFilter('to', e.target.value)} title="To" className="px-2 py-2 border border-brand-light rounded-xl text-sm bg-white" />
          </div>

          {/* Table */}
          <div className="bg-white rounded-2xl shadow-sm border border-brand-light overflow-hidden">
            <div className="px-4 py-2.5 border-b border-brand-light flex items-center justify-between">
              <p className="text-xs text-brand-neutral"><strong>{total}</strong> records{isFetching && <span className="ml-2 text-brand-neutral/60">updating…</span>}</p>
              <div className="flex items-center gap-2">
                <button onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))} disabled={offset === 0} className="p-1.5 rounded-lg border border-brand-light disabled:opacity-40 hover:bg-brand-light"><ChevronLeft className="w-4 h-4" /></button>
                <span className="text-[11px] text-brand-neutral">{total === 0 ? 0 : offset + 1}–{Math.min(offset + PAGE_SIZE, total)}</span>
                <button onClick={() => setOffset(offset + PAGE_SIZE)} disabled={offset + PAGE_SIZE >= total} className="p-1.5 rounded-lg border border-brand-light disabled:opacity-40 hover:bg-brand-light"><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-brand-primary" /></div>
            ) : rows.length === 0 ? (
              <div className="py-12 text-center text-sm text-brand-neutral">No audit records match these filters.</div>
            ) : (
              <div className="divide-y divide-brand-light/70">
                {rows.map((r) => {
                  const key = `${r.domain}-${r.id}`;
                  const isOpen = expanded === key;
                  const failed = r.status_code && r.status_code >= 400;
                  return (
                    <div key={key}>
                      <button onClick={() => setExpanded(isOpen ? null : key)} className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-brand-light/30">
                        {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-brand-neutral flex-shrink-0" /> : <ChevronRightSmall className="w-3.5 h-3.5 text-brand-neutral flex-shrink-0" />}
                        <span className="text-[11px] text-brand-neutral font-mono w-[130px] flex-shrink-0">{fmtTime(r.created_at)}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full border flex-shrink-0 w-[74px] text-center ${DOMAIN_COLORS[r.domain] || 'bg-brand-light text-brand-neutral border-brand-light'}`}>{r.domain}</span>
                        <span className="text-[11px] font-medium text-brand-dark w-[110px] flex-shrink-0 truncate">{r.action}</span>
                        <span className="text-[11px] text-brand-neutral w-[150px] flex-shrink-0 truncate" title={actorLabel(r)}>{actorLabel(r)}</span>
                        <span className="text-[12px] text-brand-dark/80 flex-1 truncate">{r.summary}</span>
                        {failed ? <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200 flex-shrink-0">{r.status_code}</span> : <span className="text-[10px] text-brand-neutral flex-shrink-0">{r.status_code || ''}</span>}
                      </button>
                      {isOpen && (
                        <div className="px-4 pb-3 pt-1 bg-[#f9fbfa] border-t border-brand-light/50">
                          <div className="text-[11px] text-brand-neutral mb-2 flex flex-wrap gap-x-4 gap-y-0.5">
                            {r.entity_type && <span>entity: <span className="font-mono text-brand-dark">{r.entity_type}{r.entity_id ? `:${r.entity_id}` : ''}</span></span>}
                            {r.location_id != null && <span>location: <span className="font-mono text-brand-dark">{r.location_id}</span></span>}
                            {r.user_role && <span>role: <span className="font-mono text-brand-dark">{r.user_role}</span></span>}
                            {r.path && <span>path: <span className="font-mono text-brand-dark">{r.method} {r.path}</span></span>}
                            {r.ip && <span>ip: <span className="font-mono text-brand-dark">{r.ip}</span></span>}
                          </div>
                          <MetaView metadata={r.metadata} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>
    </Layout>
  );
}
